import { useCallback, useEffect, useRef, useState } from 'react';
/** Current fullscreen element, standard or WebKit-prefixed. */
export function fullscreenElement() {
    return document.fullscreenElement ?? document.webkitFullscreenElement ?? null;
}
/**
 * Fullscreen on the WRAPPER (video + watermark), never the bare <video>: the
 * native fullscreen button would show the video without the watermark overlay.
 * Chromium hides that button via controlsList="nofullscreen"; where it still
 * exists (Safari ignores controlsList) we re-target to the wrapper, best effort
 * — same posture as the rest of the no-download measures.
 */
export function useWrapperFullscreen(videoRef) {
    const wrapperRef = useRef(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    // iPhone Safari has no element fullscreen at all — the button would be a no-op.
    const [isSupported, setIsSupported] = useState(true);
    useEffect(() => {
        const wrap = wrapperRef.current;
        setIsSupported(Boolean(wrap && (typeof wrap.requestFullscreen === 'function' || typeof wrap.webkitRequestFullscreen === 'function')));
    }, []);
    const toggle = useCallback(() => {
        const wrap = wrapperRef.current;
        if (fullscreenElement()) {
            const doc = document;
            if (document.exitFullscreen)
                void document.exitFullscreen().catch(() => undefined);
            else
                doc.webkitExitFullscreen?.();
        }
        else if (wrap) {
            if (wrap.requestFullscreen)
                void wrap.requestFullscreen().catch(() => undefined);
            else
                wrap.webkitRequestFullscreen?.();
        }
    }, []);
    useEffect(() => {
        const onChange = () => {
            const el = fullscreenElement();
            setIsFullscreen(Boolean(el));
            // Re-target: if the native control fullscreened the <video> itself, move the
            // fullscreen to the wrapper so the watermark stays visible. Chromium hides
            // that button via controlsList="nofullscreen", so this is a fallback path;
            // per spec requestFullscreen needs (and consumes) transient activation, so
            // the second request may be rejected — accept it and stay as we were.
            // Safari's native button may not even route through the standard API.
            if (el && el === videoRef.current && wrapperRef.current && document.exitFullscreen) {
                void document
                    .exitFullscreen()
                    .then(() => wrapperRef.current?.requestFullscreen())
                    .catch(() => undefined);
            }
        };
        document.addEventListener('fullscreenchange', onChange);
        document.addEventListener('webkitfullscreenchange', onChange);
        return () => {
            document.removeEventListener('fullscreenchange', onChange);
            document.removeEventListener('webkitfullscreenchange', onChange);
        };
    }, [videoRef]);
    /** Safari's native fullscreen button doesn't use the standard API: it enters
     *  WebKit "presentation mode" fullscreen on the bare <video> (no watermark,
     *  document.fullscreenElement stays null, so the re-target above never fires).
     *  Force it back inline — exiting needs no user activation — and offer wrapper
     *  fullscreen instead, best effort. Watermark over fullscreen UX, deliberately. */
    useEffect(() => {
        const video = videoRef.current;
        if (!video || typeof video.webkitSetPresentationMode !== 'function')
            return;
        const onModeChange = () => {
            if (video.webkitPresentationMode !== 'fullscreen')
                return;
            video.webkitSetPresentationMode?.('inline');
            const wrap = wrapperRef.current;
            if (wrap && typeof wrap.requestFullscreen === 'function') {
                void wrap.requestFullscreen().catch(() => undefined);
            }
        };
        video.addEventListener('webkitpresentationmodechanged', onModeChange);
        video.addEventListener('webkitbeginfullscreen', onModeChange);
        return () => {
            video.removeEventListener('webkitpresentationmodechanged', onModeChange);
            video.removeEventListener('webkitbeginfullscreen', onModeChange);
        };
    }, [videoRef]);
    return { wrapperRef, isFullscreen, isSupported, toggle };
}
//# sourceMappingURL=useWrapperFullscreen.js.map